import type { CSSProperties } from 'react'
import sprite from '../strevix/code/strevix-icons.svg?raw'

/*
 * Strevix icons (design/system/06-iconography.md): the system's sprite, inlined per use
 * ("prefer inlining") so the icon takes currentColor and the page needs no extra request.
 * The paths come from the ds-sync copy of code/strevix-icons.svg, parsed once at start.
 *
 * Stroke width: each path carries style="stroke-width:var(--sx-icon-stroke, 2)" with stroke-width="2"
 * as the fallback attribute (system 1792775596, AUDIT SYS-14); .sx-ico / .s16 / .s12 / .s24 set
 * --sx-icon-stroke per size.
 */

export const ICONS = [
  'state-live', 'state-start', 'state-warn', 'state-fail', 'state-idle', 'state-disabled',
  'outputs', 'signal', 'node', 'route', 'map', 'transcode', 'bitrate', 'latency', 'captions', 'language', 'chat', 'incident',
  'key', 'copy', 'eye', 'eye-off', 'external', 'refresh', 'target', 'check', 'close', 'plus',
  'chevron-right', 'chevron-down', 'collapse-left', 'search', 'tune', 'download', 'user', 'lock', 'plan', 'desktop', 'dock',
] as const
export type IconName = (typeof ICONS)[number]

interface Glyph {
  viewBox: string
  body: string
}

function parse(svg: string): Map<string, Glyph> {
  const out = new Map<string, Glyph>()
  for (const m of svg.matchAll(/<symbol id="sx-([a-z0-9-]+)" viewBox="([^"]+)">([\s\S]*?)<\/symbol>/g)) {
    out.set(m[1]!, { viewBox: m[2]!, body: m[3]! })
  }
  return out
}

const GLYPHS = parse(sprite)
for (const n of ICONS) if (!GLYPHS.has(n)) throw new Error(`strevix-icons.svg has no sx-${n}`)

const SIZE = { 12: ' s12', 16: ' s16', 20: '', 24: ' s24' } as const

/** The stroke of each size (.sx-ico, .s12, .s16, .s24 in strevix-components.css), for SVG use below. */
const STROKE = { 12: 2.75, 16: 2.4, 20: 2.2, 24: 2 } as const

/**
 * An icon inside another SVG (a map, a chart), at x/y in the parent's user units: a nested <svg>
 * with explicit size, since CSS sizes of .sx-ico do not reach a nested svg in every browser.
 */
export function SvgIcon({ name, x, y, size = 12, className }: { name: IconName; x: number; y: number; size?: 12 | 16 | 20 | 24; className?: string }) {
  const g = GLYPHS.get(name)!
  return (
    <svg x={x} y={y} width={size} height={size} viewBox={g.viewBox} aria-hidden className={className}
      style={{ '--sx-icon-stroke': STROKE[size], overflow: 'visible', transformBox: 'fill-box', transformOrigin: 'center' } as CSSProperties}
      dangerouslySetInnerHTML={{ __html: g.body }} />
  )
}

/**
 * One icon. Decorative by default (aria-hidden): the word next to it carries the meaning; pass
 * `label` for an icon that stands alone (it then gets role="img").
 */
export function Icon({ name, size = 20, className = '', label }: { name: IconName; size?: 12 | 16 | 20 | 24; className?: string; label?: string }) {
  const g = GLYPHS.get(name)!
  return (
    <svg
      className={`sx-ico${SIZE[size]}${className ? ' ' + className : ''}`}
      viewBox={g.viewBox}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      dangerouslySetInnerHTML={{ __html: g.body }}
    />
  )
}
